import { useEffect, useState } from 'react'
import { Button, Form, Modal, Switch, Tabs, Typography, message } from 'antd'
import { adminApi, formatApiError } from '../api'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import { KnowledgeFolderDocPermissions } from './KnowledgeFolderShareRules'
import './KnowledgeFolderShareRules.css'

type Props = {
  open: boolean
  folderId: number | null
  folderName: string
  scopeParams: { scope: KnowledgeScope; dimension_id?: number }
  onClose: () => void
  onSaved: () => void
}

function PlaceholderTab({ text }: { text: string }) {
  return (
    <div className="kf-folder-settings-tab-pane">
      <Typography.Text type="secondary">{text}</Typography.Text>
    </div>
  )
}

export function KnowledgeFolderSettingsModal({
  open,
  folderId,
  folderName,
  scopeParams,
  onClose,
  onSaved,
}: Props) {
  const [form] = Form.useForm<{ ai_auto_summary: boolean; ai_auto_tags: boolean }>()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const isEnterprise = scopeParams.scope === 'enterprise'

  useEffect(() => {
    if (!open || !folderId) return
    setActiveTab('basic')
    setLoading(true)
    adminApi
      .get<{ items: { id: number; ai_auto_summary?: boolean; ai_auto_tags?: boolean }[] }>(
        '/admin/knowledge/folders/labels',
        { params: scopeParams },
      )
      .then((r) => {
        const row = (r.data.items || []).find((f) => f.id === folderId)
        form.setFieldsValue({
          ai_auto_summary: !!row?.ai_auto_summary,
          ai_auto_tags: !!row?.ai_auto_tags,
        })
      })
      .catch((e) => message.error(formatApiError(e, '加载文件夹设置失败')))
      .finally(() => setLoading(false))
  }, [open, folderId, scopeParams, form])

  const saveBasic = () =>
    adminApi.put(
      `/admin/knowledge/folders/${folderId}`,
      form.getFieldsValue(),
      { params: scopeParams },
    )

  const submitBasic = () => {
    if (!folderId) return
    void form.validateFields().then(() => {
      setSaving(true)
      saveBasic()
        .then(() => {
          message.success('文件夹设置已保存')
          onSaved()
          onClose()
        })
        .catch(() => {})
        .finally(() => setSaving(false))
    })
  }

  const showBasicSaveFooter = activeTab === 'basic'

  const tabItems = [
    {
      key: 'basic',
      label: '基础设置',
      children: (
        <div className="kf-folder-settings-tab-pane">
          <Form form={form} layout="vertical" disabled={loading}>
            <Form.Item
              name="ai_auto_summary"
              label="AI 自动生成摘要"
              valuePropName="checked"
              extra="开启后，该目录下新建文档留空摘要时，将在索引完成后自动生成"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="ai_auto_tags"
              label="AI 自动生成标签"
              valuePropName="checked"
              extra="开启后，该目录下新建文档留空标签时，将在索引完成后自动生成"
            >
              <Switch />
            </Form.Item>
          </Form>
        </div>
      ),
    },
  ]

  if (isEnterprise) {
    tabItems.push(
      {
        key: 'folder-perm',
        label: '文件夹权限',
        children: <PlaceholderTab text="文件夹权限配置暂未开放。" />,
      },
      {
        key: 'doc-perm',
        label: '文档权限',
        children: (
          <div className="kf-folder-settings-tab-pane">
            <KnowledgeFolderDocPermissions
              folderId={folderId}
              scope={scopeParams.scope}
              dimensionId={scopeParams.dimension_id}
              disabled={loading}
            />
          </div>
        ),
      },
      {
        key: 'card',
        label: '卡片设置',
        children: <PlaceholderTab text="卡片设置暂未开放。" />,
      },
      {
        key: 'advanced',
        label: '高级功能',
        children: <PlaceholderTab text="高级功能暂未开放。" />,
      },
      {
        key: 'logs',
        label: '操作日志',
        children: <PlaceholderTab text="操作日志暂未开放。" />,
      },
    )
  }

  return (
    <Modal
      className={`kf-folder-settings-modal${isEnterprise ? ' kf-folder-settings-modal--enterprise' : ''}`}
      wrapClassName="kf-folder-settings-modal-wrap"
      title={`文件夹设置：${folderName}`}
      open={open}
      onCancel={onClose}
      cancelText="取消"
      destroyOnClose
      footer={
        showBasicSaveFooter ? (
          <>
            <Button onClick={onClose}>取消</Button>
            <Button type="primary" loading={saving} onClick={submitBasic}>
              保存
            </Button>
          </>
        ) : (
          <Button type="primary" onClick={onClose}>
            关闭
          </Button>
        )
      }
      centered={false}
      width={isEnterprise ? 860 : 480}
      styles={{ body: { overflow: 'hidden' } }}
    >
      {isEnterprise ? (
        <Button
          className="kf-folder-settings-modal-sync"
          type="link"
          onClick={() => message.info('同步功能暂未开放')}
        >
          同步
        </Button>
      ) : null}
      <Tabs
        className="kf-folder-settings-tabs"
        activeKey={activeTab}
        onChange={setActiveTab}
        destroyOnHidden={false}
        items={tabItems}
      />
    </Modal>
  )
}
